import { HttpClient, HttpEventType } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ApiService, BASE_CONFIG } from '@libs/core/base-config';
import { AsyncState, errorState, loadingState, successState } from '@libs/core/lib/state/async-states';
import { Failure, LocalFailure } from '@libs/core/lib/state/failure';
import { Entity } from '@libs/mtjr/shared/generated/types';
import { Observable, Subscriber, Subscription } from 'rxjs';

export type PresignedUrlResponse = {
  postURL: string;
  formData: {
    key: string;
    bucket: string;
    'x-amz-meta-userId': string;
    'Content-Type': string;
    'x-amz-date': string;
    'x-amz-algorithm': string;
    'x-amz-credential': string;
    policy: string;
    'x-amz-signature': string;
    [key: string]: string;
  };
};

@Injectable({ providedIn: 'root' })
export class UploadService {
  http = inject(HttpClient);
  apiService = inject(ApiService);
  protected resourceUrl = `${inject(BASE_CONFIG).baseUrl}/api/attachments/request-upload-url`;
  requests: Record<string, Subscription> = {};

  cancel(key: string) {
    this.requests[key]?.unsubscribe();
  }

  upload(file: File, opt: UploadOptions): Observable<AsyncState<Entity.Attachment>> {
    return new Observable<AsyncState<Entity.Attachment>>((observer) => {
      if (opt?.maxSizeInMb && file.size > opt.maxSizeInMb * 1024 * 1024) {
        observer.next(errorState(new LocalFailure('File size exceeds the maximum limit.')));
        observer.complete();
        return;
      }

      observer.next(loadingState());

      this.requests[opt.key] = this.fetchPresignedUrl({
        contentType: file.type || 'image/jpeg',
        contentLength: file.size,
        directory: 'attachments/',
        ext: this.getFileExtension(file),
        description: 'Product description',
      }).subscribe({
        next: (res) => {
          this._handleUpload(res, file, observer, opt);
        },
        error: (error) => {
          console.error('[Upload] Presigned URL error:', error);
          observer.next(errorState(Failure.fromHttpError(error)));
          observer.complete();
        },
      });
    });
  }

  _handleUpload(
    res: PresignedUrlResponse,
    file: File,
    observer: Subscriber<AsyncState<Entity.Attachment>>,
    opt: UploadOptions,
  ) {
    const formData = new FormData();
    Object.keys(res.formData).forEach((key) => {
      formData.append(key, res.formData[key]);
    });
    formData.append('file', file, file.name || 'image.jpg');
    const request = this.http.post(res.postURL, formData, {
      observe: 'events',
      reportProgress: true,
    });

    this.requests[opt.key] = request.subscribe({
      next: (event) => {
        if (event.type === HttpEventType.UploadProgress) {
          const progress = Math.round((100 * event.loaded) / (event.total ?? file.size));
          observer.next(loadingState(progress));
        } else if (event.type === HttpEventType.Response) {
          observer.next(
            successState({
              id: '',
              key: res.formData.key,
              url: res.postURL.replaceAll(':443', '') + '/' + res.formData.key,
              userId: '',
              orgId: '',
              fakeForTesting: false,
            }),
          );
          observer.complete();
        }
      },
      error: (error) => {
        console.error('[Upload] S3 upload error:', error);
        observer.next(errorState(Failure.fromHttpError(error)));
      },
    });
  }

  fetchPresignedUrl(params: PresignedUrlParams) {
    return this.http.post<PresignedUrlResponse>(this.resourceUrl, params);
  }

  private getFileExtension(file: File): string {
    // Try to get extension from filename first
    if (file.name && file.name.includes('.')) {
      const ext = file.name.split('.').pop();
      if (ext && ext.length <= 5) {
        return `.${ext.toLowerCase()}`;
      }
    }

    // Fallback: derive extension from MIME type (important for iOS camera captures)
    const mimeToExt: Record<string, string> = {
      'image/jpeg': '.jpg',
      'image/jpg': '.jpg',
      'image/png': '.png',
      'image/gif': '.gif',
      'image/webp': '.webp',
      'image/heic': '.heic',
      'image/heif': '.heif',
      'image/svg+xml': '.svg',
      'image/bmp': '.bmp',
      'image/tiff': '.tiff',
    };

    return mimeToExt[file.type] || '.jpg';
  }
}

export type PresignedUrlParams = {
  ext: string;
  directory: string;
  contentType: string;
  contentLength: number;
  description: string;
};

export type UploadOptions = { vendorId?: string; key: string; maxSizeInMb?: number };
